import React, { useState, useEffect } from 'react';
import { getQuotations, deleteQuotation, updateQuotationStatus } from '../api/quotationsApi';
import { FaEdit, FaFilePdf, FaCheck } from 'react-icons/fa';
import { Trash2 } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { generateQuotationPDF } from '../utils/pdfGenerator';
import { useNavigate } from 'react-router-dom';

const QuotationCenter = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const data = await getQuotations();
      setQuotations(data);
    } catch (error) {
      toast.error('Failed to fetch quotations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setQuotationToDelete(id);
    setDeleteModalOpen(true);
  };

  const executeDelete = async () => {
    if (!quotationToDelete) return;
    try {
      await deleteQuotation(quotationToDelete);
      toast.success('Quotation deleted successfully');
      setDeleteModalOpen(false);
      setQuotationToDelete(null);
      fetchQuotations();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setQuotationToDelete(null);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateQuotationStatus(id, newStatus);
      toast.success('Status updated');
      fetchQuotations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEdit = (quotation) => {
    navigate('/price-management', { state: { quotationId: quotation._id, quotationData: quotation } });
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'Accepted': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Exported': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Quotation Center</h1>
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
                    <td className="px-6 py-4 whitespace-nowrap text-right"><div className="h-6 bg-gray-200 rounded w-24 ml-auto"></div></td>
                  </tr>
                ))
              ) : quotations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-sm text-gray-500">
                    No quotations found.
                  </td>
                </tr>
              ) : (
                quotations.map((q) => (
                  <tr key={q._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(q.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {q.customerName}
                      <span className="ml-2 text-xs text-gray-400 uppercase">({q.language})</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {q.items?.length || 0} items
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${(q.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select 
                        value={q.status}
                        onChange={(e) => handleStatusChange(q._id, e.target.value)}
                        disabled={q.status !== 'Draft'}
                        className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed ${getStatusBadgeClass(q.status)}`}
                      >
                        <option value="Draft">Draft</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Exported">Exported</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                      <button onClick={() => generateQuotationPDF(q)} className="text-blue-600 hover:text-blue-900" title="Download PDF">
                        <FaFilePdf size={18} />
                      </button>
                      <button 
                        onClick={() => handleEdit(q)} 
                        className="text-blue-600 hover:text-blue-900" 
                        title="Edit Quotation"
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                       onClick={() => confirmDelete(q._id)} 
                       className="text-red-600 hover:text-red-900" 
                       title="Delete Quotation"
                       >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Quotation?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this quotation? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationCenter;
